import type { QueryFunctionContext } from '@tanstack/react-query';
import qs from 'qs';
import bookingInstance from '@/lib/axios/booking-instance';
import { handleRequest } from '@/lib/api/factories/base-api-factory';
import type { IBaseResponse } from '@/types/base-response';
import type { IAgeGroupPreset, IGroupSizePreset } from './price-preset.types';
import {
  toAgeGroupPresetPayload,
  toGroupSizePresetPayload,
  type AgeGroupPresetValues,
  type GroupSizePresetValues,
} from './price-preset.utils';

const listParams = (params: unknown) => ({
  params,
  paramsSerializer: (value: Record<string, unknown>) =>
    qs.stringify(value, { arrayFormat: 'repeat' }),
});

const getAgeGroupPresetList = ({ queryKey }: QueryFunctionContext) =>
  handleRequest<IBaseResponse<IAgeGroupPreset[]>>(
    bookingInstance.get('/age-group-preset', listParams(queryKey[1]))
  );

const getAgeGroupPresetById = ({ queryKey }: QueryFunctionContext) =>
  handleRequest<IBaseResponse<IAgeGroupPreset>>(
    bookingInstance.get(`/age-group-preset/${queryKey[1]}`)
  );

const createAgeGroupPreset = (values: AgeGroupPresetValues) =>
  handleRequest(
    bookingInstance.post(
      '/age-group-preset',
      toAgeGroupPresetPayload(values, { create: true })
    )
  );

const editAgeGroupPreset = ({
  id,
  data,
}: {
  id: string;
  data: AgeGroupPresetValues;
}) =>
  handleRequest(
    bookingInstance.put(
      `/age-group-preset/${id}`,
      toAgeGroupPresetPayload(data, { create: false })
    )
  );

const getGroupSizePresetList = ({ queryKey }: QueryFunctionContext) =>
  handleRequest<IBaseResponse<IGroupSizePreset[]>>(
    bookingInstance.get('/group-size-preset', listParams(queryKey[1]))
  );

const getGroupSizePresetById = ({ queryKey }: QueryFunctionContext) =>
  handleRequest<IBaseResponse<IGroupSizePreset>>(
    bookingInstance.get(`/group-size-preset/${queryKey[1]}`)
  );

const createGroupSizePreset = (values: GroupSizePresetValues) =>
  handleRequest(
    bookingInstance.post(
      '/group-size-preset',
      toGroupSizePresetPayload(values, { create: true })
    )
  );

const editGroupSizePreset = ({
  id,
  data,
}: {
  id: string;
  data: GroupSizePresetValues;
}) =>
  handleRequest(
    bookingInstance.put(
      `/group-size-preset/${id}`,
      toGroupSizePresetPayload(data, { create: false })
    )
  );

const PricePresetService = {
  getAgeGroupPresetList,
  getAgeGroupPresetById,
  createAgeGroupPreset,
  editAgeGroupPreset,
  getGroupSizePresetList,
  getGroupSizePresetById,
  createGroupSizePreset,
  editGroupSizePreset,
};

export { PricePresetService };
